/* Sticky header colour adaptation + footer pull-up.
   The header is fixed and transparent; its ink must contrast with whatever
   section sits under it. Each section declares data-header-theme="light|dark".
   An IntersectionObserver watches a 1px band at the header's vertical centre
   and flips .is-light on the header when a light section crosses that line.
   Pull-up: once the fullscreen footer takes over (its top crosses 20% from
   the viewport top — the reference's clamp(top 20%) ScrollTrigger), the
   header slides up out of view; scrolling back brings it down again.

   Registry-managed: the header persists across Barba navigations but the
   sections/footer it watches are swapped — every init re-queries and rebuilds
   the observer; the previous page's listeners die with the abort signal. */
(() => {
  const init = (ctx) => {
    const signal = ctx && ctx.signal;
    const header = document.querySelector('.onni-header');
    if (!header) return;

    const sections = [...document.querySelectorAll('[data-header-theme]')];
    if (!sections.length) return;

    let observer = null;

    const setTheme = (theme) => {
      header.classList.toggle('is-light', theme === 'light');
    };

    const onIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setTheme(entry.target.getAttribute('data-header-theme'));
        }
      });
    };

    const build = () => {
      if (observer) observer.disconnect();
      const center = header.offsetHeight / 2;
      const vh = window.innerHeight;
      observer = new IntersectionObserver(onIntersect, {
        // shrink the viewport to a 1px line at the header's centre
        rootMargin: `-${center}px 0px -${Math.max(vh - center - 1, 0)}px 0px`,
        threshold: 0,
      });
      sections.forEach((section) => observer.observe(section));
    };

    build();

    // recompute the detection line when the viewport (and header height) changes
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 150);
    }, { signal });

    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(resizeTimer);
        if (observer) observer.disconnect();
      });
    }

    /* ---------- footer pull-up ----------
       Condition-based toggle = the reference's onEnter/onLeaveBack pair in one:
       true while the footer's top sits above the 20% line, false again as soon
       as it drops back below (scrolling up). No footer on this page (contact)
       → drop the state so the header can't stay hidden after navigating. */
    const footer = document.querySelector('[data-footer]');
    if (footer) {
      const pullUp = () => {
        header.classList.toggle(
          'is-pulled',
          footer.getBoundingClientRect().top <= window.innerHeight * 0.2
        );
      };
      window.addEventListener('scroll', pullUp, { passive: true, signal });
      window.addEventListener('resize', pullUp, { passive: true, signal });
      pullUp();
    } else {
      header.classList.remove('is-pulled');
    }
  };

  if (window.Onni && window.Onni.register) {
    window.Onni.register('header-theme', init);
  } else {
    init();
  }
})();
